import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import type { z } from 'zod';
import { commissionRuleInput, type CommissionRuleDto } from '@barbeiropro/contracts';
import { FormModal } from '@/components/shared/form-modal';
import { Field } from '@/components/shared/field';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { useBarbers } from '@/lib/org';
import { useSaveRule, useServiceOptions } from '../api';
import { handleFormError } from '../form-utils';
import { MoneyInput } from '../money-input';

type Values = z.input<typeof commissionRuleInput>;
const ANY = 'any';

const bpsToText = (bps: number) => String(bps / 100).replace('.', ',');

/** Percentual digitado em % (ex.: "12,5") guardado em basis points. */
function PercentInput({ id, value, onChange, invalid }: { id: string; value: number; onChange: (bps: number) => void; invalid: boolean }) {
  const [text, setText] = useState(value ? bpsToText(value) : '');
  return (
    <div className="relative">
      <Input
        id={id}
        inputMode="decimal"
        value={text}
        aria-invalid={invalid}
        className="pr-8"
        onChange={(e) => {
          const t = e.target.value.replace(/[^\d,]/g, '');
          setText(t);
          const n = Number(t.replace(',', '.'));
          onChange(Number.isFinite(n) ? Math.round(n * 100) : 0);
        }}
      />
      <span aria-hidden className="pointer-events-none absolute top-1/2 right-3 -translate-y-1/2 text-sm text-muted-foreground">%</span>
    </div>
  );
}

export function RuleModal({ rule, onClose }: { rule: CommissionRuleDto | 'new'; onClose: () => void }) {
  const editing = rule === 'new' ? null : rule;
  const barbers = useBarbers();
  const services = useServiceOptions();
  const save = useSaveRule();
  const form = useForm<Values>({
    resolver: zodResolver(commissionRuleInput),
    defaultValues: {
      barberId: editing?.barberId ?? null,
      serviceId: editing?.serviceId ?? null,
      type: editing?.type ?? 'percent',
      value: editing?.value ?? 0,
      priority: editing?.priority ?? 0,
      active: editing?.active ?? true,
    },
  });
  const { errors } = form.formState;
  const type = form.watch('type');

  async function submit(values: Values) {
    if (values.type === 'percent' && values.value > 10000) {
      form.setError('value', { message: 'O percentual não pode passar de 100%.' });
      return;
    }
    try {
      await save.mutateAsync({
        id: editing?.id,
        body: { barberId: values.barberId ?? null, serviceId: values.serviceId ?? null, type: values.type, value: values.value, priority: values.priority ?? 0, active: values.active ?? true },
      });
      toast.success(editing ? 'Regra atualizada.' : 'Regra criada.');
      onClose();
    } catch (e) {
      handleFormError(e, form);
    }
  }

  return (
    <FormModal
      open
      onOpenChange={(v) => !v && onClose()}
      title={editing ? 'Editar regra de comissão' : 'Nova regra de comissão'}
      description="Deixe profissional e serviço em branco para criar uma regra geral."
      footer={
        <>
          <Button type="button" variant="outline" onClick={onClose}>
            Voltar
          </Button>
          <Button type="submit" form="rule-form" disabled={save.isPending}>
            {save.isPending ? 'Salvando…' : 'Salvar regra'}
          </Button>
        </>
      }
    >
      <form id="rule-form" onSubmit={form.handleSubmit(submit)} className="grid gap-4 sm:grid-cols-2" noValidate>
        <Field id="rule-barber" label="Profissional">
          <Controller
            control={form.control}
            name="barberId"
            render={({ field }) => (
              <Select value={field.value ?? ANY} onValueChange={(v) => field.onChange(v === ANY ? null : v)}>
                <SelectTrigger id="rule-barber">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ANY}>Todos os profissionais</SelectItem>
                  {(barbers.data ?? []).map((b) => (
                    <SelectItem key={b.id} value={b.id}>
                      {b.displayName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </Field>
        <Field id="rule-service" label="Serviço">
          <Controller
            control={form.control}
            name="serviceId"
            render={({ field }) => (
              <Select value={field.value ?? ANY} onValueChange={(v) => field.onChange(v === ANY ? null : v)}>
                <SelectTrigger id="rule-service">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ANY}>Todos os serviços</SelectItem>
                  {(services.data ?? []).map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </Field>
        <Field id="rule-type" label="Tipo de comissão">
          <Controller
            control={form.control}
            name="type"
            render={({ field }) => (
              <Select
                value={field.value}
                onValueChange={(v) => {
                  field.onChange(v);
                  form.setValue('value', 0);
                }}
              >
                <SelectTrigger id="rule-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="percent">Percentual do valor recebido</SelectItem>
                  <SelectItem value="fixed">Valor fixo por atendimento</SelectItem>
                </SelectContent>
              </Select>
            )}
          />
        </Field>
        <Field id="rule-value" label={type === 'percent' ? 'Percentual' : 'Valor fixo'} error={errors.value?.message}>
          <Controller
            control={form.control}
            name="value"
            render={({ field }) =>
              type === 'percent' ? (
                <PercentInput key="p" id="rule-value" value={field.value} onChange={field.onChange} invalid={!!errors.value} />
              ) : (
                <MoneyInput key="f" id="rule-value" value={field.value} onChange={field.onChange} aria-invalid={!!errors.value} />
              )
            }
          />
        </Field>
        <Field id="rule-priority" label="Prioridade" hint="Desempata regras igualmente específicas. Maior vence." error={errors.priority?.message}>
          <Input id="rule-priority" type="number" min={0} max={100} aria-invalid={!!errors.priority} {...form.register('priority', { valueAsNumber: true })} />
        </Field>
        <div className="flex items-center gap-2 self-end pb-2">
          <Controller control={form.control} name="active" render={({ field }) => <Switch id="rule-active" checked={field.value ?? true} onCheckedChange={field.onChange} />} />
          <Label htmlFor="rule-active">Regra ativa</Label>
        </div>
      </form>
    </FormModal>
  );
}
